import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';

interface Candidate {
  id: string;
  score: number | null;
  ai_rating: number | null;
}

interface PerformanceComparisonProps {
  currentCandidateScore: number;
  assessmentId: string;
  candidateId: string;
}

const PerformanceComparison: React.FC<PerformanceComparisonProps> = ({
  currentCandidateScore,
  assessmentId,
  candidateId
}) => {
  const [comparisonData, setComparisonData] = React.useState<{
    averageScore: number;
    totalCandidates: number;
    rank: number;
    percentile: number;
  }>({
    averageScore: 0,
    totalCandidates: 0,
    rank: 0,
    percentile: 0
  });

  React.useEffect(() => {
    const fetchComparisonData = async () => {
      try {
        // Fetch all completed candidates for this assessment
        const { data: candidates, error } = await supabase
          .from('candidates')
          .select('id, score, ai_rating')
          .eq('assessment_id', assessmentId)
          .eq('status', 'completed')
          .not('id', 'eq', candidateId); // Exclude current candidate

        if (error) throw error;

        if (!candidates?.length) {
          setComparisonData({
            averageScore: currentCandidateScore,
            totalCandidates: 1,
            rank: 1,
            percentile: 100
          });
          return;
        }

        // Calculate metrics
        const scores = candidates.map((c: Candidate) => (c.ai_rating || c.score || 0) * 20); // Convert 0-5 rating to percentage
        const allScores = [...scores, currentCandidateScore].sort((a: number, b: number) => b - a);
        
        const averageScore = scores.reduce((acc: number, score: number) => acc + score, 0) / scores.length;
        const rank = allScores.indexOf(currentCandidateScore) + 1;
        const totalCandidates = allScores.length;
        const percentile = ((totalCandidates - rank) / totalCandidates) * 100;

        setComparisonData({
          averageScore,
          totalCandidates,
          rank,
          percentile
        });
      } catch (error) {
        console.error('Error fetching comparison data:', error);
        // Fallback to default values on error
        setComparisonData({
          averageScore: currentCandidateScore,
          totalCandidates: 1,
          rank: 1,
          percentile: 100
        });
      }
    };

    fetchComparisonData();
  }, [assessmentId, candidateId, currentCandidateScore]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Performance Comparison</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium">Current Score</span>
              <span className="text-sm">{currentCandidateScore.toFixed(1)}%</span>
            </div>
            <Progress value={currentCandidateScore} className="h-2" variant="gradient" />
          </div>
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium">Average Score</span>
              <span className="text-sm">{comparisonData.averageScore.toFixed(1)}%</span>
            </div>
            <Progress value={comparisonData.averageScore} className="h-2" variant="gradient" />
          </div>
          <div className="pt-2">
            <p className="text-sm">
              Ranked <span className="font-medium">#{comparisonData.rank}</span> out of{' '}
              <span className="font-medium">{comparisonData.totalCandidates}</span> candidates
            </p>
            <p className="text-sm text-muted-foreground">
              Top {comparisonData.percentile.toFixed(1)}% percentile
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PerformanceComparison; 